import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GasRangeCalendar, useGasI18n, useGasNotify } from '../../shared/kit';
import type { GasCalendarDayState } from '../../shared/kit';
import {
    configureApi,
    createReservation,
    createReservationsBatch,
    fetchAvailability,
    fetchBatchCalendar,
    fetchItem,
    fingerprintCartAttempt,
    joinWaitlist,
    newIdempotencyKey,
    resolveAttemptKey,
} from './api';
import type { AttemptKeyState, BatchCalendar, LineAvailability } from './api';
import { clearCart, clearDraftMeta, clearLineDates, getCart, getDraftMeta, lineKey, removeByKey, setItemImage, setQtyByKey, subscribe } from './store';
import type { RentalCartItem } from './store';
import type { CartInitData } from './types';

const DAY_MS = 86400000;
function ymdKey(d: Date): string {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function pad(n: number) { return String(n).padStart(2, '0'); }
function ymd(d: Date) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function serverDateTime(d: Date | null, time: string): string {
    if (!d) return '';
    const tt = /^\d{2}:\d{2}$/.test(time) ? time : '09:00';
    return `${ymd(d)} ${tt}:00`;
}
function fmtDate(d: Date | null): string {
    return d ? d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
}

/** Parse un datetime serveur "YYYY-MM-DD HH:MM:SS" ou ISO en Date locale (jour). */
function parseServerDay(raw: string): Date | null {
    const m = raw.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
}

function parseServerTime(raw: string, fallback: string): string {
    const m = raw.trim().match(/\b(\d{2}):(\d{2})(?::\d{2})?\b/);
    return m ? `${m[1]}:${m[2]}` : fallback;
}

export default function App({ initData }: { initData: CartInitData }) {
    const { t } = useGasI18n();
    const notify = useGasNotify();
    const adminMode = initData.pickupMode === 'admin';

    const [items, setItems] = useState<RentalCartItem[]>(getCart());
    const [start, setStart] = useState<Date | null>(null);
    const [end, setEnd] = useState<Date | null>(null);
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('18:00');
    const [avail, setAvail] = useState<Record<string, LineAvailability>>({});
    const [batchCal, setBatchCal] = useState<BatchCalendar | null>(null);
    const [focusId, setFocusId] = useState<number | null>(null);
    const [checking, setChecking] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [doneRefs, setDoneRefs] = useState<number[] | null>(null);
    const [termsAccepted, setTermsAccepted] = useState(!!initData.termsAlreadyAccepted);
    const [searchQueryId, setSearchQueryId] = useState<string>('');
    const [fromSearch, setFromSearch] = useState(false);
    const reserveAttemptRef = useRef<AttemptKeyState | null>(null);
    const waitlistAttemptKeysRef = useRef<Record<string, string>>({});

    function onTermsChange(checked: boolean): void {
        // Toute interaction conditions invalide la tentative en cours.
        reserveAttemptRef.current = null;
        setTermsAccepted(checked);
    }

    useEffect(() => {
        configureApi(initData.restBase, initData.nonce);
        const unsub = subscribe(() => setItems(getCart()));
        // Pré-remplit les dates + query_id depuis un brouillon constrained_search.
        const meta = getDraftMeta();
        if (meta) {
            const s = parseServerDay(meta.start_at);
            const e = parseServerDay(meta.end_at);
            if (s) setStart(s);
            if (e) setEnd(e);
            if (!adminMode) {
                setStartTime(parseServerTime(meta.start_at, '09:00'));
                setEndTime(parseServerTime(meta.end_at, '18:00'));
            }
            if (meta.query_id) {
                setSearchQueryId(meta.query_id);
                setFromSearch(true);
            }
        }
        return unsub;
    }, [initData.restBase, initData.nonce, adminMode]);

    // Résout l'image manquante depuis l'item (panier ajouté avant la photo, ou
    // import sans vignette stockée) → setItemImage persiste + re-render.
    const missingImgKey = items.filter((i) => !i.image_url).map((i) => i.item_id).join(',');
    useEffect(() => {
        let cancelled = false;
        items.filter((i) => !i.image_url).forEach((it) => {
            fetchItem(it.item_id)
                .then((info) => { if (!cancelled && info?.image_url) { setItemImage(it.item_id, info.image_url); } })
                .catch(() => { /* placeholder conservé */ });
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missingImgKey]);

    // Calendrier batch (dispo par jour et par produit) pour colorer le calendrier.
    const itemIdsKey = items.map((i) => i.item_id).join(',');
    useEffect(() => {
        if (items.length === 0) { setBatchCal(null); return; }
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const to = new Date(today.getTime() + 180 * DAY_MS);
        fetchBatchCalendar(items.map((i) => i.item_id), `${ymd(today)} 00:00:00`, `${ymd(to)} 00:00:00`)
            .then(setBatchCal)
            .catch(() => setBatchCal(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemIdsKey]);

    // Occupation par produit et par jour (clé ymd → quantité occupée).
    const occByItemDay = useMemo(() => {
        const map: Record<number, Record<string, number>> = {};
        if (!batchCal) return map;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        for (const [idStr, info] of Object.entries(batchCal.items)) {
            const id = Number(idStr);
            const days: Record<string, number> = {};
            for (let i = 0; i < 185; i++) {
                const ds = new Date(today.getTime() + i * DAY_MS);
                const de = new Date(ds.getTime() + DAY_MS);
                let occ = 0;
                for (const b of info.busy) {
                    const bs = new Date(b.start.replace(' ', 'T')).getTime();
                    const be = new Date(b.end.replace(' ', 'T')).getTime();
                    if (bs < de.getTime() && be > ds.getTime()) occ += b.quantity;
                }
                days[ymdKey(ds)] = occ;
            }
            map[id] = days;
        }
        return map;
    }, [batchCal]);

    function remainingFor(itemId: number, day: string): number {
        const cap = batchCal?.items[String(itemId)]?.capacity ?? 0;
        const occ = occByItemDay[itemId]?.[day] ?? 0;
        return Math.max(0, cap - occ);
    }

    // État d'un jour : focus produit → sa dispo ; sinon agrégat de tout le panier.
    function getDayState(d: Date): GasCalendarDayState {
        if (!batchCal) return {};
        const key = ymdKey(d);
        if (focusId !== null) {
            const it = items.find((x) => x.item_id === focusId);
            const need = it?.qty ?? 1;
            const rem = remainingFor(focusId, key);
            const cap = batchCal.items[String(focusId)]?.capacity ?? 0;
            return {
                tone: rem >= need ? 'free' : rem > 0 ? 'partial' : 'full',
                disabled: rem <= 0,
                badge: cap > 1 && rem > 0 ? rem : undefined,
            };
        }
        // Agrégat : combien d'articles du panier sont servables ce jour.
        let okCount = 0;
        for (const it of items) {
            if (remainingFor(it.item_id, key) >= it.qty) okCount++;
        }
        const tone = okCount === items.length ? 'free' : okCount > 0 ? 'partial' : 'full';
        return {
            tone,
            badge: items.length > 1 ? `${okCount}/${items.length}` : undefined,
        };
    }

    const serverStart = serverDateTime(start, adminMode ? '09:00' : startTime);
    const serverEnd = serverDateTime(end, adminMode ? '18:00' : endTime);
    const rangeReady = !!serverStart && !!serverEnd && serverEnd > serverStart;

    // Période EFFECTIVE d'une ligne : la sienne (location en 2 temps), sinon
    // les dates globales du panier.
    const lineRange = (it: RentalCartItem): { s: string; e: string } | null => {
        if (it.start_at && it.end_at) return { s: it.start_at, e: it.end_at };
        return rangeReady ? { s: serverStart, e: serverEnd } : null;
    };
    const datedLines = items.filter((it) => it.start_at && it.end_at);
    const undatedLines = items.filter((it) => !(it.start_at && it.end_at));

    // Récupère la dispo de chaque ligne pour SA période effective.
    useEffect(() => {
        const ready = items.filter((it) => lineRange(it) !== null);
        if (ready.length === 0) { setAvail({}); return; }
        let cancelled = false;
        setChecking(true);
        Promise.all(ready.map((it) => {
            const r = lineRange(it)!;
            return fetchAvailability(it.item_id, r.s, r.e, it.qty)
                .then((a) => ({ key: lineKey(it), a }))
                .catch(() => ({ key: lineKey(it), a: { available: 0, quote: null } as LineAvailability }));
        })).then((results) => {
            if (cancelled) return;
            const map: Record<string, LineAvailability> = {};
            results.forEach((r) => { map[r.key] = r.a; });
            setAvail(map);
        }).finally(() => { if (!cancelled) setChecking(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverStart, serverEnd, items.map((i) => `${lineKey(i)}:${i.qty}`).join(',')]);

    const lineState = (it: RentalCartItem) => {
        const r = lineRange(it);
        const a = avail[lineKey(it)];
        if (!r || !a) return { ok: false, available: 0, partial: false };
        return { ok: a.available >= it.qty, available: a.available, partial: a.available > 0 && a.available < it.qty };
    };

    const total = useMemo(() => {
        return items.reduce((s, it) => {
            const a = avail[lineKey(it)];
            const ok = lineRange(it) && a && a.available >= it.qty && a.quote && !a.quote.free;
            return s + (ok ? a!.quote!.applied : 0);
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, avail, rangeReady, serverStart, serverEnd]);

    const reservableLines = items.filter((it) => lineState(it).ok);

    // Segments = groupes de lignes réservables par période effective.
    // Chaque segment devient UNE réservation — créées ensemble en une caisse.
    const segments = useMemo(() => {
        const groups = new Map<string, { start_at: string; end_at: string; lines: RentalCartItem[] }>();
        for (const it of reservableLines) {
            const r = lineRange(it)!;
            const key = `${r.s}|${r.e}`;
            const g = groups.get(key) ?? { start_at: r.s, end_at: r.e, lines: [] };
            g.lines.push(it);
            groups.set(key, g);
        }
        return Array.from(groups.values()).sort((a, b) => a.start_at.localeCompare(b.start_at));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservableLines.map((i) => `${lineKey(i)}:${i.qty}`).join(','), serverStart, serverEnd]);

    const termsOk = !initData.termsRequired || initData.termsAlreadyAccepted || termsAccepted;
    // Les lignes datées se réservent même sans dates globales : le calendrier
    // n'est requis que s'il reste des lignes non datées.
    const canSubmit = reservableLines.length > 0 && !submitting && initData.isLogged && termsOk
        && (undatedLines.length === 0 || rangeReady);

    async function submit() {
        const payloadSegments = segments.map((seg) => ({
            start_at: seg.start_at,
            end_at: seg.end_at,
            lines: seg.lines.map((it) => ({ item_id: it.item_id, quantity: it.qty })),
        }));
        const fingerprint = fingerprintCartAttempt({
            segments: payloadSegments,
            terms_accepted: termsOk,
            query_id: searchQueryId,
        });
        const attempt = resolveAttemptKey(reserveAttemptRef.current, fingerprint);
        reserveAttemptRef.current = attempt;
        setSubmitting(true);
        try {
            // La réservation réelle se fait ICI (tunnel), jamais depuis la recherche.
            // Le query_id opaque est conservé jusqu'à la réservation pour mesurer
            // la conversion sans recopier la requête ni une donnée personnelle.
            let ids: number[];
            if (payloadSegments.length === 1) {
                const seg = payloadSegments[0];
                const res = await createReservation(
                    seg.start_at,
                    seg.end_at,
                    seg.lines,
                    termsOk,
                    searchQueryId,
                    attempt.key,
                );
                ids = [res.reservation_id];
            } else {
                const res = await createReservationsBatch(
                    payloadSegments,
                    termsOk,
                    searchQueryId,
                    attempt.key,
                );
                ids = res.reservation_ids;
            }
            reserveAttemptRef.current = null;
            // Retire du panier les lignes réservées.
            reservableLines.forEach((it) => removeByKey(lineKey(it)));
            clearDraftMeta();
            setSearchQueryId('');
            setFromSearch(false);
            setDoneRefs(ids);
            notify.success(t('rental.reserve.success'));
        } catch (e) {
            notify.error((e as Error).message);
        } finally {
            setSubmitting(false);
        }
    }

    async function waitlist(it: RentalCartItem) {
        const r = lineRange(it);
        if (!r) return;
        const keyId = lineKey(it);
        try {
            if (!waitlistAttemptKeysRef.current[keyId]) {
                waitlistAttemptKeysRef.current[keyId] = newIdempotencyKey();
            }
            const attemptKey = waitlistAttemptKeysRef.current[keyId];
            const res = await joinWaitlist(it.item_id, r.s, r.e, attemptKey);
            delete waitlistAttemptKeysRef.current[keyId];
            notify.success(res.already ? t('rental.waitlist.already') : t('rental.waitlist.joined'));
        } catch (e) { notify.error((e as Error).message); }
    }

    if (doneRefs) {
        return (
            <div className="gas-rental-cart gas-rental-cart--done">
                <h1>{t('rental.cart.title')}</h1>
                <p className="gas-rental-cart__success">
                    {doneRefs.length > 1
                        ? t('rental.reserve.success_detail_n', { n: String(doneRefs.length) })
                        : t('rental.reserve.success_detail')}
                </p>
                <p><a className="gas-button gas-button--primary" href={initData.catalogUrl}>{t('rental.public.back')}</a></p>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="gas-rental-cart gas-rental-cart--empty">
                <h1>{t('rental.cart.title')}</h1>
                <p>{t('rental.cart.empty')}</p>
                <a className="gas-button gas-button--primary" href={initData.catalogUrl}>{t('rental.cart.browse')}</a>
            </div>
        );
    }

    return (
        <div className="gas-rental-cart">
            <h1>{t('rental.cart.title')}</h1>
            {fromSearch ? (
                <p className="gas-rental-cart__from-search">
                    {t('rental.cart.from_search')}
                </p>
            ) : null}

            <div className="gas-rental-cart__layout">
                <section className="gas-rental-cart__lines">
                    <h2>{t('rental.cart.items')}</h2>
                    <ul>
                        {items.map((it) => {
                            const st = lineState(it);
                            const key = lineKey(it);
                            const dated = !!(it.start_at && it.end_at);
                            // Stock total du produit (capacité du pool) : borne le sélecteur de quantité.
                            const cap = Math.max(1, batchCal?.items[String(it.item_id)]?.capacity ?? it.qty);
                            return (
                                <li
                                    key={key}
                                    className={`gas-rental-cart__line${focusId === it.item_id ? ' is-focused' : ''}${dated ? ' gas-rental-cart__line--dated' : ''}`}
                                    onMouseEnter={() => setFocusId(it.item_id)}
                                    onMouseLeave={() => setFocusId(null)}
                                    onFocus={() => setFocusId(it.item_id)}
                                    onBlur={() => setFocusId(null)}
                                >
                                    {it.image_url
                                        ? <img
                                            className="gas-rental-cart__thumb"
                                            src={it.image_url}
                                            alt=""
                                            onError={(e) => {
                                                if (initData.placeholderUrl) e.currentTarget.src = initData.placeholderUrl;
                                                else e.currentTarget.style.visibility = 'hidden';
                                            }}
                                        />
                                        : <span className="gas-rental-cart__thumb gas-rental-cart__thumb--ph" aria-hidden="true">🧰</span>}
                                    <div className="gas-rental-cart__line-main">
                                        <strong>{it.name}</strong>
                                        {dated && (
                                            <span className="gas-rental-cart__line-dates">
                                                {t('rental.cart.line_dates', {
                                                    from: fmtDate(parseServerDay(it.start_at!)),
                                                    to: fmtDate(parseServerDay(it.end_at!)),
                                                })}
                                                <button
                                                    type="button"
                                                    className="gas-rental-cart__line-dates-clear"
                                                    onClick={() => clearLineDates(key)}
                                                    title={t('rental.cart.line_dates_clear')}
                                                >
                                                    {t('rental.cart.line_dates_clear')}
                                                </button>
                                            </span>
                                        )}
                                        <div className="gas-rental-cart__line-qty">
                                            <label className="gas-rental-cart__qty-label">{t('rental.cart.qty')}
                                                {cap > 1 ? (
                                                    <select
                                                        className="gas-rental-cart__qty"
                                                        value={Math.min(it.qty, cap)}
                                                        onChange={(e) => setQtyByKey(key, Number(e.target.value) || 1)}
                                                    >
                                                        {Array.from({ length: cap }, (_, i) => i + 1).map((n) => (
                                                            <option key={n} value={n}>{n}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span className="gas-rental-cart__qty-fixed">1</span>
                                                )}
                                            </label>
                                            <button type="button" className="gas-rental-cart__remove" onClick={() => removeByKey(key)}>{t('common.remove')}</button>
                                        </div>
                                        {lineRange(it) !== null && (
                                            checking ? (
                                                <span className="gas-rental-cart__avail">{t('rental.reserve.checking')}</span>
                                            ) : st.ok ? (
                                                <span className="gas-rental-cart__avail gas-rental-cart__avail--ok">
                                                    {t('rental.cart.available_n', { n: String(st.available) })}
                                                    {avail[key]?.quote && !avail[key]!.quote!.free
                                                        ? ` · ${avail[key]!.quote!.applied.toFixed(2)} CHF`
                                                        : ` · ${t('rental.public.free')}`}
                                                </span>
                                            ) : (
                                                <span className="gas-rental-cart__avail gas-rental-cart__avail--ko">
                                                    {st.partial
                                                        ? t('rental.cart.partial_n', { n: String(st.available), need: String(it.qty) })
                                                        : t('rental.reserve.unavailable')}
                                                    {initData.isLogged && (
                                                        <button type="button" className="gas-rental-cart__waitlist" onClick={() => waitlist(it)}>
                                                            {t('rental.waitlist.join')}
                                                        </button>
                                                    )}
                                                </span>
                                            )
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    <button type="button" className="gas-rental-cart__clear" onClick={() => { clearCart(); }}>{t('rental.cart.clear')}</button>
                </section>

                <section className="gas-rental-cart__when">
                    <h2>{t('rental.cart.dates')}</h2>
                    <p className="gas-rental-cart__cal-caption">
                        {focusId !== null
                            ? t('rental.cart.cal_focus', { name: items.find((x) => x.item_id === focusId)?.name ?? '' })
                            : t('rental.cart.cal_all')}
                    </p>
                    <GasRangeCalendar
                        start={start}
                        end={end}
                        onSelectRange={(s, e) => { setStart(s); setEnd(e); }}
                        locale={initData.locale}
                        getDayState={getDayState}
                        legend={[
                            { tone: 'free', label: t('rental.calendar.free') },
                            { tone: 'partial', label: t('rental.calendar.partial') },
                            { tone: 'full', label: t('rental.calendar.full') },
                        ]}
                    />
                    <div className="gas-rental-cart__range">
                        <div>
                            <span className="gas-rental-cart__range-label">{t('rental.reserve.start')}</span>
                            <strong>{fmtDate(start)}</strong>
                            {!adminMode && <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} aria-label={t('rental.reserve.start_time')} />}
                        </div>
                        <div>
                            <span className="gas-rental-cart__range-label">{t('rental.reserve.end')}</span>
                            <strong>{fmtDate(end)}</strong>
                            {!adminMode && <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} aria-label={t('rental.reserve.end_time')} />}
                        </div>
                    </div>
                    {!rangeReady && (
                        <p className="gas-rental-cart__date-hint">
                            {start && !end ? t('rental.cart.pick_end_hint') : t('rental.cart.pick_dates_hint')}
                        </p>
                    )}
                    {adminMode && <p className="gas-rental-cart__hint">{t('rental.reserve.pickup_admin_hint')}</p>}

                    {total > 0 && (
                        <p className="gas-rental-cart__total">{t('rental.reserve.total')} : <strong>{total.toFixed(2)} CHF</strong></p>
                    )}

                    {!initData.isLogged ? (
                        <p className="gas-rental-cart__login">
                            {t('rental.reserve.login_required')}{' '}
                            {initData.loginUrl && <a href={initData.loginUrl}>{t('rental.reserve.login_link')}</a>}
                        </p>
                    ) : (
                        <>
                            {initData.termsRequired && (
                                <div className="gas-rental-cart__terms">
                                    {initData.termsAlreadyAccepted ? (
                                        <p>{t('rental.terms.already_accepted')}</p>
                                    ) : (
                                        <label>
                                            <input type="checkbox" checked={termsAccepted} onChange={(e) => onTermsChange(e.target.checked)} />
                                            <span>{t(initData.termsMode === 'remember' ? 'rental.terms.accept_remember' : 'rental.terms.accept_once')}</span>
                                        </label>
                                    )}
                                    {initData.termsText ? <details><summary>{t('rental.terms.read')}</summary><p>{initData.termsText}</p></details> : null}
                                </div>
                            )}
                            {segments.length > 1 && (
                                <div className="gas-rental-cart__segments">
                                    <p className="gas-rental-cart__segments-title">{t('rental.cart.segments_title', { n: String(segments.length) })}</p>
                                    <ul>
                                        {segments.map((seg) => (
                                            <li key={`${seg.start_at}|${seg.end_at}`}>
                                                {t('rental.cart.segment_line', {
                                                    from: fmtDate(parseServerDay(seg.start_at)),
                                                    to: fmtDate(parseServerDay(seg.end_at)),
                                                    n: String(seg.lines.reduce((s2, l) => s2 + l.qty, 0)),
                                                })}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <button type="button" className="gas-button gas-button--primary gas-rental-cart__submit" disabled={!canSubmit} onClick={submit}>
                                {submitting
                                    ? t('rental.reserve.submitting')
                                    : segments.length > 1
                                        ? t('rental.cart.reserve_segments', { n: String(segments.length) })
                                        : t('rental.cart.reserve_n', { n: String(reservableLines.length) })}
                            </button>
                        </>
                    )}
                    {reservableLines.length < items.length && (rangeReady || undatedLines.length === 0) && (
                        <p className="gas-rental-cart__note">{t('rental.cart.some_unavailable')}</p>
                    )}
                </section>
            </div>
        </div>
    );
}
